"use client";

import { zodResolver } from "@hookform/resolvers/zod";
import { generate } from "generate-password-ts";
import { useRouter, useSearchParams } from "next/navigation";
import { Suspense, useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";

import PasswordInput from "~/lib/shadcn/components/password-input";
import { Button } from "~/lib/shadcn/components/ui/button";
import { Checkbox } from "~/lib/shadcn/components/ui/checkbox";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "~/lib/shadcn/components/ui/form";
import { Input } from "~/lib/shadcn/components/ui/input";
import { PhoneInput } from "~/lib/shadcn/components/ui/phone-input";
import { authClient } from "~/lib/utils/better-auth-client";

import type { InvitationSchemaType, MembershipTypes } from "../../../(application)/admin/create-invitation/schema";

import { InvitationSchema } from "../../../(application)/admin/create-invitation/schema";

export default function CreateApplicationForm({
	buttonValue = "Send Invitation",
	membershipType,
}: {
	buttonValue?: string;
	membershipType: MembershipTypes[number];
	type?: string;
}) {
	return (
		<Suspense>
			<CreateApplicationFormWithoutSuspense buttonValue={buttonValue} membershipType={membershipType} />
		</Suspense>
	);
}

function CreateApplicationFormWithoutSuspense({
	buttonValue = "Send Invitation",
	membershipType,
}: {
	buttonValue?: string;
	membershipType: MembershipTypes[number];
	type?: string;
}) {
	const searchParams = useSearchParams();
	const utmDetails = {
		utm_campaign: searchParams.get("utm_campaign"),
		utm_content: searchParams.get("utm_content"),
		utm_medium: searchParams.get("utm_medium"),
		utm_source: searchParams.get("utm_source"),
		utm_term: searchParams.get("utm_term"),
	} as const;

	const router = useRouter();
	const [visible, setVisible] = useState<boolean>(false);
	const form = useForm<InvitationSchemaType>({
		defaultValues: {
			email: "",
			membership: membershipType,
			name: "",
			password: "",
			phoneNumber: undefined,
			referrer: JSON.stringify(utmDetails),
			signUpToMailingList: true,
		},
		resolver: zodResolver(InvitationSchema),
	});
	async function onSubmit(values: InvitationSchemaType) {
		const { signUpToMailingList, ...signUpData } = values;
		const { error } = await authClient.signUp.email({
			...signUpData,
			membership: membershipType,
		});
		if (signUpToMailingList) {
			// TODO Create Klaviyo email client side
			// "Signed up to mailing list"
		}
		if (error) {
			toast.error(error.message ?? "Something went wrong.");
			// @ts-expect-error error.response
			console.error(error, error.response);
			return;
		}
		toast.success("Thank you!");
		router.push("/onboarding/thank-you");
	}
	return (
		<Form {...form}>
			<form className="space-y-6 max-w-screen-sm" onSubmit={form.handleSubmit(onSubmit)}>
				<FormField
					control={form.control}
					name="referrer"
					render={({ field }) => (
						<FormItem className="hidden">
							<FormLabel>Referrer</FormLabel>
							<FormControl>
								<Input placeholder="Referrer" readOnly {...field} />
							</FormControl>
							<FormMessage />
						</FormItem>
					)}
				/>
				<FormField
					control={form.control}
					name="name"
					render={({ field }) => (
						<FormItem>
							<FormLabel>Name</FormLabel>
							<FormControl>
								<Input placeholder="Name" {...field} />
							</FormControl>
							{/* <FormDescription>This is your public display name.</FormDescription> */}
							<FormMessage />
						</FormItem>
					)}
				/>
				<FormField
					control={form.control}
					name="email"
					render={({ field }) => (
						<FormItem>
							<FormLabel>Email</FormLabel>
							<FormControl>
								<Input autoComplete="email" placeholder="Email" {...field} />
							</FormControl>
							{/* <FormDescription>This is your public display name.</FormDescription> */}
							<FormMessage />
						</FormItem>
					)}
				/>
				<FormField
					control={form.control}
					name="phoneNumber"
					render={({ field }) => (
						<FormItem>
							<FormLabel>Phone Number</FormLabel>
							<FormControl>
								<PhoneInput {...field} value={field.value ?? undefined} />
							</FormControl>
							{/* <FormDescription>This is your public display name.</FormDescription> */}
							<FormMessage />
						</FormItem>
					)}
				/>
				<FormField
					control={form.control}
					name="password"
					render={({ field }) => (
						<FormItem>
							<div>
								<div className="inline-flex w-full justify-between">
									<FormLabel>Password</FormLabel>
									<button
										className="text-xs underline underline-offset-4 text-inherit hover:text-inherit font-light leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
										onClick={() => {
											const password = generate({
												excludeSimilarCharacters: true,
												length: 12,
												lowercase: true,
												// symbols: true,
												numbers: true,
												uppercase: true,
											});
											form.setValue("password", password);
											setVisible(true);
										}}
										type="button"
									>
										Generate Password?
									</button>
								</div>
							</div>
							<FormControl>
								<PasswordInput visible={visible} {...field} />
							</FormControl>
							<FormMessage />
						</FormItem>
					)}
				/>
				<FormField
					control={form.control}
					name="signUpToMailingList"
					render={({ field }) => (
						<FormItem className="inline-flex justify-center gap-2 overflow-hidden items-center">
							<FormControl>
								<Checkbox checked={field.value} onCheckedChange={field.onChange} />
							</FormControl>
							<FormLabel className="leading-none !mt-0">Sign up to Mailing List?</FormLabel>
							{/* <FormDescription>This is your public display name.</FormDescription> */}
							<FormMessage />
						</FormItem>
					)}
				/>
				<Button
					className="block rounded-none !mt-8 font-skia px-8 text-black uppercase tracking-widest bg-[#D0CDC3] hover:bg-[#D0CDC3]"
					disabled={form.formState.isSubmitting || form.formState.isLoading || form.formState.isValidating}
					type="submit"
				>
					{buttonValue}
				</Button>
			</form>
		</Form>
	);
}
